import React, { useState, useEffect, useMemo, useRef } from "react";
import Container from "@/components/marketing/Container";
import { Search, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { base44 } from "@/api/base44Client";
import { track } from "@/lib/analytics";
import SectionHeading from "@/components/marketing/SectionHeading";
import CTASection from "@/components/marketing/CTASection";
import MediaNetworkCard from "@/components/marketing/service/MediaNetworkCard";
import { getPackageRegions } from "@/lib/regions";
import Reveal from "@/components/marketing/Reveal";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

/**
 * Continental region roll-up. Maps each stored geo value (country/state or
 * an already-continental string) to one of seven buckets. Values already
 * equal to a bucket name map to themselves; unknown/empty values fall back
 * to "Global" so no outlet ever becomes invisible under region filtering.
 */
const REGION_BUCKETS = [
  "Global",
  "North America",
  "Europe",
  "Middle East",
  "Asia Pacific",
  "Africa",
  "Latin America",
];

const COUNTRY_TO_REGION = {
  // North America
  "United States": "North America",
  Canada: "North America",
  // Europe
  "United Kingdom": "Europe",
  Netherlands: "Europe",
  Spain: "Europe",
  Italy: "Europe",
  Germany: "Europe",
  Monaco: "Europe",
  Ukraine: "Europe",
  Moldova: "Europe",
  Bulgaria: "Europe",
  Hungary: "Europe",
  Europe: "Europe",
  // Middle East
  "United Arab Emirates": "Middle East",
  "Saudi Arabia": "Middle East",
  Israel: "Middle East",
  Turkey: "Middle East",
  // Asia Pacific
  Philippines: "Asia Pacific",
  "South Korea": "Asia Pacific",
  Japan: "Asia Pacific",
  Australia: "Asia Pacific",
  China: "Asia Pacific",
  India: "Asia Pacific",
  "Hong Kong": "Asia Pacific",
  "Asia Pacific": "Asia Pacific",
  // Africa
  Ghana: "Africa",
  Uganda: "Africa",
  Nigeria: "Africa",
  Kenya: "Africa",
  Senegal: "Africa",
  Africa: "Africa",
  // Latin America
  Mexico: "Latin America",
  Brazil: "Latin America",
  Colombia: "Latin America",
  // Global
  Global: "Global",
};

function mapToRegion(value) {
  if (!value) return "Global";
  const v = String(value).trim();
  if (!v) return "Global";
  if (REGION_BUCKETS.includes(v)) return v;
  return COUNTRY_TO_REGION[v] || "Global";
}

/** Derive the unique continental buckets an outlet belongs to. Empty geo
 *  data rolls up to ["Global"] so the outlet always appears under Global. */
function deriveRegionBuckets(regions) {
  if (!regions || regions.length === 0) return ["Global"];
  const set = new Set();
  regions.forEach((r) => set.add(mapToRegion(r)));
  return Array.from(set);
}

function deriveNewswireRegion(pkg) {
  const raw = pkg.newswire_region;
  if (raw === "Gulf States" || raw === "MENAP" || raw === "Middle East") return "Middle East";
  if (raw && raw.startsWith("Africa")) return "Africa";
  if (raw && ["Asia Pacific", "Southeast Asia", "ASEAN", "Greater China", "Greater Bay Area"].includes(raw)) return "Asia Pacific";
  if (raw === "Europe" || raw === "UK") return "Europe";
  if (raw === "Latin America") return "Latin America";
  if (raw === "North America") return "North America";
  if (raw === "Global") return "Global";
  const name = (pkg.name || "").toLowerCase();
  const countries = pkg.countries || [];
  if (name.includes("global") || name.includes("globe") || name.includes("premium") || name.includes("standard")) return "Global";
  if (countries.length >= 3) return "Global";
  if (countries.some((c) => ["USA", "United States", "Canada"].includes(c))) return "North America";
  if (countries.some((c) => ["UK", "United Kingdom"].includes(c))) return "Europe";
  return "Global";
}

function deriveNewswireSector(pkg) {
  const name = (pkg.name || "").toLowerCase();
  if (name.includes("crypto")) return "Crypto";
  if (name.includes("finance")) return "Finance";
  return null;
}

const TYPE_OPTIONS = ["All", "Placements", "Newswires"];
// Fixed continental filter chips — individual countries are rolled up into
// these buckets via mapToRegion at filter time.
const REGION_FILTER_OPTIONS = [
  "All",
  "Global",
  "North America",
  "Europe",
  "Middle East",
  "Asia Pacific",
  "Africa",
  "Latin America",
];
const SECTOR_MIN_COUNT = 3; // prune genre chips with fewer than this many outlets
const PAGE_SIZE = 60;       // client-side pagination page size
const FULL_LIMIT = 2000;    // fetch the entire active, retail-visible catalogue in one pass

export default function MediaNetwork() {
  // Full catalogue (placements + newswires), fetched once. All filters run
  // against this complete set so matching records beyond the first page are
  // never hidden.
  const [allOutlets, setAllOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("All");
  const [sectorFilter, setSectorFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const searchTimerRef = useRef(null);

  const buildOutlets = (placements, newswires) => {
    const placementMap = new Map();
    (placements || []).forEach((p) => {
      const existing = placementMap.get(p.name);
      if (!existing || (!existing.logo_url && p.logo_url)) {
        placementMap.set(p.name, p);
      }
    });
    const placementOutlets = Array.from(placementMap.values()).map((p) => {
      const regions = getPackageRegions(p); // geo_locations with geo_location fallback
      const genres = Array.isArray(p.publication_genres) && p.publication_genres.length
        ? p.publication_genres
        : (p.publication_genre ? [p.publication_genre] : []);
      const sectors = genres.map(capitalize).filter(Boolean);
      return {
        name: p.name,
        favicon: p.logo_url || null,
        regions,
        regionBuckets: deriveRegionBuckets(regions),
        region: regions[0] || "Global",
        sectors,
        sector: sectors[0] || null,
        tier: p.is_tier_1 ? "Tier 1 Publication" : null,
        type: "placement",
        has_outlet_page: !!p.has_outlet_page,
      };
    });
    const newswireOutlets = (newswires || []).map((p) => {
      const r = deriveNewswireRegion(p);
      const s = deriveNewswireSector(p);
      return {
        name: p.name,
        favicon: p.logo_url || null,
        regions: r ? [r] : [],
        regionBuckets: r ? [mapToRegion(r)] : ["Global"],
        region: r || "Global",
        sectors: s ? [s] : [],
        sector: s,
        tier: "Newswire",
        type: "newswire",
      };
    });
    return [...placementOutlets, ...newswireOutlets].sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      base44.entities.Package.filter(
        { category: "guaranteed_placements", is_active: true, retail_visible: { $ne: false } },
        "name",
        FULL_LIMIT
      ),
      base44.entities.Package.filter(
        { category: "newswires", is_active: true, retail_visible: { $ne: false } },
        "name",
        FULL_LIMIT
      ),
    ])
      .then(([placements, newswires]) => {
        if (!active) return;
        setAllOutlets(buildOutlets(placements, newswires));
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      track("media_network_search", { query: val, filter_used: "search" });
    }, 600);
  };

  // Genre/sector chip options: derived from the FULL catalogue, pruned to
  // genres with at least SECTOR_MIN_COUNT outlets so thin filters don't show
  // near-empty results.
  const sectors = useMemo(() => {
    const counts = new Map();
    allOutlets.forEach((o) => o.sectors.forEach((s) => counts.set(s, (counts.get(s) || 0) + 1)));
    const kept = Array.from(counts.entries())
      .filter(([, c]) => c >= SECTOR_MIN_COUNT)
      .map(([s]) => s)
      .sort();
    return ["All", ...kept];
  }, [allOutlets]);

  // Filtering against the complete catalogue. Region matches if ANY of the
  // outlet's continental buckets equals the selected region (multi-region
  // outlets appear under each of their regions). Empty geo data rolled up to
  // "Global" via deriveRegionBuckets, so such outlets are never hidden.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allOutlets.filter((o) => {
      if (q && !(o.name || "").toLowerCase().includes(q)) return false;
      if (typeFilter === "Placements" && o.type !== "placement") return false;
      if (typeFilter === "Newswires" && o.type !== "newswire") return false;
      if (regionFilter !== "All" && !o.regionBuckets.includes(regionFilter)) return false;
      if (sectorFilter !== "All" && !o.sectors.includes(sectorFilter)) return false;
      return true;
    });
  }, [allOutlets, search, typeFilter, regionFilter, sectorFilter]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, typeFilter, regionFilter, sectorFilter]);

  const displayed = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;
  const loadMore = () => setVisibleCount((c) => c + PAGE_SIZE);

  const pillBase = "px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm transition-colors";
  const pillActive = "bg-cobalt text-white";
  const pillInactive = "bg-slate border border-border text-steel hover:text-bone";

  return (
    <div className="pt-24 lg:pt-32 bg-canvas">
      <Container>
        <Reveal>
          <SectionHeading
            tag="Media Network"
            title="1,700+ Publications Worldwide"
            subtitle="Our distribution network spans premium and niche media outlets across the US, UK, Europe, the Middle East, and Asia Pacific."
          />
        </Reveal>

        {/* Filters */}
        <div className="mb-8 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel" />
              <Input
                placeholder="Search outlets..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 bg-slate border-border text-bone placeholder:text-steel"
              />
            </div>
            <div className="flex items-center gap-2">
              {TYPE_OPTIONS.map((t) => (
                <button
                  key={t}
                  onClick={() => { setTypeFilter(t); track("media_network_search", { query: search, filter_used: t }); }}
                  className={`${pillBase} ${typeFilter === t ? pillActive : pillInactive}`}
                >
                  {t === "All" ? "All Types" : t}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {REGION_FILTER_OPTIONS.map((r) => (
              <button
                key={r}
                onClick={() => { setRegionFilter(r); track("media_network_search", { query: search, filter_used: r }); }}
                className={`${pillBase} ${regionFilter === r ? pillActive : pillInactive}`}
              >
                {r}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {sectors.map((s) => (
              <button
                key={s}
                onClick={() => { setSectorFilter(s); track("media_network_search", { query: search, filter_used: s }); }}
                className={`${pillBase} ${sectorFilter === s ? pillActive : pillInactive}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-20">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="bg-slate/30 border border-border rounded-lg h-20 animate-pulse" />
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-16 text-steel mb-20">
            <Globe className="w-12 h-12 mx-auto mb-4 text-steel/50" />
            <p>No outlets match your search. Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              {displayed.map((outlet) => (
                <MediaNetworkCard key={`${outlet.name}-${outlet.type}`} outlet={outlet} />
              ))}
            </div>
            {hasMore ? (
              <div className="flex justify-center mb-20">
                <Button variant="outline" onClick={loadMore} disabled={false}>
                  Load More
                </Button>
              </div>
            ) : (
              <p className="text-center text-steel text-sm mb-20">
                {search
                  ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""} for "${search}".`
                  : `End of list — ${filtered.length} outlets shown.`}
              </p>
            )}
          </>
        )}
      </Container>
      <CTASection />
    </div>
  );
}